'use client';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { cn } from '@/app/utils';

interface SlidingCarouselProps {
  items: ReactNode[];
  className?: string;
  /** 是否启用滚动捕捉 */
  enableScrollSnap?: boolean;
  /** 滚动捕捉类型 */
  scrollSnapType?: 'mandatory' | 'proximity';
  /** 可选的 key 生成函数，用于避免使用数组索引 */
  getItemKey?: (item: ReactNode, index: number) => string;
}

export function SlidingCarousel({
  items,
  className,
  enableScrollSnap = false,
  scrollSnapType = 'proximity',
  getItemKey,
}: SlidingCarouselProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      e.preventDefault();
      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = (x - startX) * 2; // 滚动速度倍数
      containerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        'overflow-x-auto overflow-y-hidden',
        // 隐藏滚动条但保持滚动功能
        'hide-scrollbar',
        // 平滑滚动
        'scroll-smooth',
        // 桌面端拖拽样式
        'cursor-grab select-none',
        isDragging && 'cursor-grabbing',
        // 滚动捕捉
        enableScrollSnap && (scrollSnapType === 'mandatory' ? 'snap-x snap-mandatory' : 'snap-x snap-proximity'),
        className,
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex">
        {items.map((item, index) => (
          <div
            key={getItemKey ? getItemKey(item, index) : `carousel-item-${index}`}
            className={cn(
              'flex-shrink-0',
              // 滚动捕捉点
              enableScrollSnap && 'snap-start',
            )}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}
